import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent15: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Design business model</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Design business model
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  In this step you will examine existing models across industries for capturing some of the value your product brings to your customer, and then use the work you have done in other steps to brainstorm an innovative model for your venture. Why spend all this time focusing on innovation related to technology and product design without a commensurate amount of time on innovating with your business model? The track record shows that companies that spend time and effort on innovative business models can see enormous payback.

Once you have established a business model, it is possible but generally not easy to change to a different model. Therefore, choose a business model that distinguishes you from competitors and gives you an advantage over them, because they cannot easily change their business model to match yours.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Detail key business model factors
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  When thinking about an appropriate business model for your business, there is no one universally right answer, as it depends on your specific situation. There are few key factors that every entrepreneur should consider:

Customers—understand what the customer will be willing to do. The knowledge you gained from mapping the Decision-Making Unit and Process to Acquire a Paying Customer will be valuable here.
Value creation—understand how much value your product provides to your customer and when.
Value capturing—determine which ways of capturing value match up well. Your Quantified Value Proposition will help here.
Competition—identify what your competition is doing
Distribution—determine if your distribution channel has the right incentives to sell your product.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Determine the main type of business model
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  Thinking through some of the common types of business models will help you get a better sense of which is the best fit for you. You may eventually decide on a hybrid business model that includes elements from multiple categories. It is a good idea to look at business models in industries other than your own; such lateral innovation often results in creative, effective business models.

One-time up-front charge plus maintenance. This is the most common business model, where a customer pays a large up-front charge to obtain the product, with the option to secure ongoing upgrades or maintenance of the product for a recurring fee.
Cost plus. In this model, the customer pays a set percentage above the cost of producing the product. This is common in government contracts as well as situations where you and your customer want to share the risk of producing the product.
Hourly Rates. This model also tends to reward activity as opposed to progress, which can be the wrong incentive, but when a project is poorly defined or very dynamic, this might well be the preferred model. A common business model for services firms, it is similar to the Cost plus model, but the rates are set by the market demand rather than costs.
Subscription or leasing. This is a set payment each month or another predetermined and agreed-upon time period. It is a great way to get a recurring revenue stream. There are a number of variations, including annual or multi-year commitment, month-to-month commitment etc.
Licensing your IP to customers and receiving a royalty can result in a very high gross margin (gross margin is the difference between marginal revenue and marginal costs). In addition, if you are licensing your product, you do not have to make big investments in production and distribution capability for a whole product.
Consumables. Another value capture framework that can be advantageous to both the customer and your business is the consumables model. For the customer, the benefit is a low up-front cost, with ongoing costs based on usage, which the customer can usually control.
Upsell with high-margin products. Similar to the consumable business model, the central product is sold at a very low margin, but the overall margin is increased from the sale of very high-margin add-on products. This business model is often used in consumer electronics stores or websites and frequently in new car sales.
Advertising. The ability to attract and retain a desirable demographic can be monetized through third parties who want access to the customers you have attracted. When done properly and on a sufficient scale, this can be a very lucrative model, as Google and others have shown.
Reselling the collected data (or temporary access to it). Somewhat similar to the advertising model, reselling user data requires first attracting end users with a free product, then receiving money from third parties who pay for access to demographic and other information about your users.
Transaction fee. Online retailers often pay or receive a commission for referrals that lead to sales. The model is similar to how credit card companies work, where a percentage of each transaction goes to the credit card company.
Usage-based. A usage-based model— similar to how electric utilities are metered— has been used across various other industries, including cloud computing products today (such as Amazon Web Services).
"Cell phone plan". This is a predictable, recurring base fee charged in exchange for a certain amount of committed usage, with additional charges, often at much higher marginal rates, if the customer uses more than their allotted amount. The base charge is generally far less per amount of usage than the overage charge.
Parking meter or penalty charges. This is the same business model used by parking meters, credit card companies and (for a while) Blockbuster by charging late fees. Do not take advantage of your customer’s naivete as a central pillar of your business model, as this is a risky strategy.
Microtransactions. A new successful model that came into vogue with online computer games, and is now being tested to try to save newspapers, is microtransactions. In this model, the customer is asked to provide their credit card and then they make very small transactions for digital goods (which have virtually no marginal cost because they are electrons). There are many of them so they can add up.
Shared savings. This business model is often brainstormed, but rarely used because of the complexities in implementing it, despite its conceptual elegance. In this scenario, the customer pays only once they have realized savings or benefits from the product. It is generally not implemented because it is hard to determine how much savings to attribute to the product, especially over a multi-year time period.
Franchise. If an entrepreneur comes up with a good idea and is able to implement but does not have the desire, skills, or money to roll it out, they can use the franchise model and get paid a percentage of sales and/ or receive a large initial startup fee in return for providing the knowledge and brand that has been developed. You can also make money by selling your brand-name products to the franchises to be distributed.
Operating and maintenance. A new business might not want to really sell a product but rather get paid for running a plant or other operation for a fee. While this is similar in some ways to a consulting agreement, the customer has more incentives to control or cut costs, as it will directly impact the customer’s income.
Other. This is nowhere near an exhaustive list of business models, but it will help you think about different ways to capture value for your business.
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>

  );
};

export { StepContent15 };
